import { FormEvent, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  getEcosystemProvisionStatus,
  onEcosystemProvisionTerminal,
  previewEcosystemProvision,
  startEcosystemProvision,
  type EcosystemProvisionPayload,
  type EcosystemService,
} from '@frontend/api-clients/adminEcosystemProvisionClient';
import type { AdminPackageAvailability } from '@frontend/api-clients/ecosystemsClient';
import PlatformServicesChecklist from '@frontend/components/projects/PlatformServicesChecklist';
import { useProvisionPoll } from '../../../hooks/useProvisionPoll';
import ProvisionRunView from './ProvisionRunView';

type WizardPhase = 'form' | 'review' | 'running';

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export interface CreateEcosystemWizardProps {
  onSuccess?: (ecosystemId: string) => void;
  onCancel?: () => void;
  packageAvailability: AdminPackageAvailability | null;
}

export default function CreateEcosystemWizard({
  onSuccess,
  onCancel,
  packageAvailability,
}: CreateEcosystemWizardProps) {
  const [phase, setPhase] = useState<WizardPhase>('form');
  const [id, setId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [memberGroup, setMemberGroup] = useState('');
  const [giteaOrg, setGiteaOrg] = useState('');
  const [services, setServices] = useState<EcosystemService[]>(() => {
    const installed = new Set(packageAvailability?.installedServices || []);
    return (['gitea', 'nextcloud', 'woodpecker'] as EcosystemService[]).filter((service) =>
      installed.has(service)
    );
  });
  const [plannedSteps, setPlannedSteps] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const payload = (): EcosystemProvisionPayload => ({
    id: id.trim(),
    displayName: displayName.trim(),
    memberGroup: (memberGroup || `${id.trim()}-users`).trim(),
    giteaOrg: (giteaOrg || id.trim()).trim(),
    services,
  });

  const { status, error: pollError, polling } = useProvisionPoll({
    token,
    correlationId,
    fetchStatus: getEcosystemProvisionStatus,
    enabled: phase === 'running' && Boolean(correlationId),
    onTerminal: (s) => {
      onEcosystemProvisionTerminal(s.runState);
      if (s.runState === 'completed') {
        onSuccess?.(id.trim());
      }
    },
  });

  const onIdBlur = () => {
    const slug = id.trim();
    if (!slug) return;
    if (!memberGroup) setMemberGroup(`${slug}-users`);
    if (!giteaOrg) setGiteaOrg(slug);
  };

  const validateForm = (): string | null => {
    const slug = id.trim();
    if (!slug) return 'Ecosystem id is required.';
    if (!SLUG_RE.test(slug)) return 'Use lowercase letters, numbers, and single hyphens only.';
    if (!displayName.trim()) return 'Display name is required.';
    if (services.length === 0) return 'Select at least one service.';
    return null;
  };

  const onReview = async (event: FormEvent) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const t = authClient.getToken();
      if (!t) throw new Error('You must be signed in.');
      const preview = await previewEcosystemProvision(t, payload());
      setPlannedSteps(preview.plannedSteps);
      setPhase('review');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const onStart = async () => {
    setBusy(true);
    setError('');
    try {
      const t = authClient.getToken();
      if (!t) throw new Error('You must be signed in.');
      const start = await startEcosystemProvision(t, payload());
      setToken(t);
      setCorrelationId(start.correlationId);
      setPhase('running');
    } catch (e) {
      const err = e as Error & { correlationId?: string };
      if (err.correlationId) setCorrelationId(err.correlationId);
      setError(err.message || 'Failed to start provisioning');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPhase('form');
    setId('');
    setDisplayName('');
    setMemberGroup('');
    setGiteaOrg('');
    setServices(['gitea', 'nextcloud', 'woodpecker']);
    setPlannedSteps([]);
    setError('');
    setCorrelationId(null);
    setToken(null);
  };

  if (phase === 'running') {
    return (
      <div className="admin-orch-wizard">
        <ProvisionRunView
          title="Ecosystem provision status"
          status={status}
          pollError={pollError}
          loading={polling && !status}
        />
        {status && (status.runState === 'completed' || status.runState === 'failed' || status.runState === 'partial') ? (
          <div className="admin-create-user-actions">
            {status.runState === 'completed' ? (
              <button type="button" className="btn btn-primary" onClick={() => onSuccess?.(id.trim())}>
                Done
              </button>
            ) : (
              <button type="button" className="btn btn-secondary" onClick={reset}>
                Start over
              </button>
            )}
            {onCancel ? (
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Close
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (phase === 'review') {
    return (
      <div className="admin-orch-wizard">
        <h4 className="admin-orch-subtitle">Review planned steps</h4>
        <p className="ecosystem-description">
          Policy-only fields (no branding in v1). Confirm to start orchestrated provisioning.
        </p>
        <ul className="admin-sync-status-list">
          {plannedSteps.map((step) => (
            <li key={step}>
              <span className="admin-sync-status-label">{step.replace(/_/g, ' ')}</span>
            </li>
          ))}
        </ul>
        <dl className="admin-orch-summary">
          <dt>Id</dt>
          <dd>
            <code className="admin-inline-code">{id.trim()}</code>
          </dd>
          <dt>Display name</dt>
          <dd>{displayName.trim()}</dd>
          <dt>Member group</dt>
          <dd>
            <code className="admin-inline-code">{(memberGroup || `${id.trim()}-users`).trim()}</code>
          </dd>
          <dt>Services</dt>
          <dd>{services.join(', ')}</dd>
        </dl>
        {error ? <div className="settings-message settings-error" role="alert">{error}</div> : null}
        <div className="admin-create-user-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={onStart}>
            {busy ? 'Starting…' : 'Start provisioning'}
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setPhase('form')}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="admin-orch-wizard" onSubmit={onReview}>
      <p className="ecosystem-description">
        Creates ecosystem policy and provisions Keycloak, Gitea, Woodpecker, and Nextcloud via portal
        APIs.
      </p>
      <label className="admin-orch-field" htmlFor="eco-orch-id">
        <span>Ecosystem id</span>
        <input
          id="eco-orch-id"
          type="text"
          value={id}
          onChange={(e) => setId(e.target.value)}
          onBlur={onIdBlur}
          placeholder="my-ecosystem"
          autoComplete="off"
          required
        />
      </label>
      <label className="admin-orch-field" htmlFor="eco-orch-display-name">
        <span>Display name</span>
        <input
          id="eco-orch-display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
      </label>
      <label className="admin-orch-field">
        <span>Keycloak member group</span>
        <input
          type="text"
          value={memberGroup}
          onChange={(e) => setMemberGroup(e.target.value)}
          placeholder={`${id.trim() || 'id'}-users`}
        />
      </label>
      <label className="admin-orch-field">
        <span>Gitea org</span>
        <input
          type="text"
          value={giteaOrg}
          onChange={(e) => setGiteaOrg(e.target.value)}
          placeholder={id.trim() || 'org'}
        />
      </label>
      <PlatformServicesChecklist
        idPrefix="create-ecosystem-service"
        legend="Services"
        packageCatalog={packageAvailability?.catalog}
        availableServices={packageAvailability?.installedServices || []}
        realmServices={packageAvailability?.realmServices || []}
        ecosystemServices={[]}
        selectedServices={services}
        canAddRealmServices
        canAddEcosystemServices
        onChange={(next) => setServices(next as EcosystemService[])}
      />
      {error ? <div className="settings-message settings-error" role="alert">{error}</div> : null}
      <div className="admin-create-user-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Loading preview…' : 'Review steps'}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
